import { Box, Card, CardActions, CardContent, CardHeader, CircularProgress, IconButton, Stack, TextField, Tooltip } from "@mui/material";
import { QRCodeCanvas } from "qrcode.react";
import { WifiKeyDto } from "../../dtos/WifiKeyDto";
import { formatDateTime } from "../../utils";
import { useContext, useEffect, useState } from "react";
import VspPortalContext from "../../store/VspPortalContext";
import { LoadingButton } from "@mui/lab";
import LockResetIcon from '@mui/icons-material/LockReset';
import { SelectVillageDialog } from "./dialogs/SelectVillageDialog";
import { useVillageWifiHttp } from "./hooks/useVillageWifiHttp";
import WifiIcon from '@mui/icons-material/Wifi';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ByWifiKeyDialog } from "./dialogs/ByWifiKeyDialog";
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import { ByWifiKeyDto } from "../../dtos/ByWifiKeyDto";
import { ResidentDto } from "../../dtos/ResidentDto";


type WifiDetailsComponentProps = {

    deviceSessionsClick?: ( residentId : number ) => void;
    resident?: ResidentDto;
    wifiKeyDto : WifiKeyDto;

};


const textFieldSx = {

    marginTop: '10px', 
    width: '420px',
    label: {
        WebkitTextFillColor: 'black !important'
    },
    div: {
        input: {
            WebkitTextFillColor: 'black !important'
        }
    }
    
};


export const WifiDetails : React.FC< WifiDetailsComponentProps > = ( props: WifiDetailsComponentProps ) => {

    const { deviceSessionsClick, resident, wifiKeyDto: initialWifiKeyDto } = props;

    const [ isLoading, setIsLoading ] = useState< boolean >( false );
    const [ isLoadingForDevice, setIsLoadingForDevice ] = useState< boolean >( false );
    const [ byWifiKeyDto, setByWifiKeyDto ] = useState< ByWifiKeyDto >( );
    const [ isResettingPassword, setIsResettingPassword ] = useState< boolean >( false );
    const [ moreWifiDetailsDialogOpen, setMoreWifiDetailsDialogOpen  ] = useState< boolean >( false );
    const [ selectVillageDialogOpen, setSelectVillageDialogOpen ] = useState< boolean >( false );
    const [ wifiKey, setWifiKey ] = useState< string >( '' );
    const [ wifiKeyDto, setWifiKeyDto ] = useState<WifiKeyDto>( initialWifiKeyDto );

    const { state } = useContext( VspPortalContext );

    const { getResidentByWifiKey, resetResidentWifiKey } = useVillageWifiHttp( );


    useEffect( () => {

        const wifiKey = `WIFI:S:${wifiKeyDto.ssid};T:WPA;P:${wifiKeyDto.psk};`
        setWifiKey( wifiKey );

    }, [ wifiKeyDto ] );



    const handleDeviceSessionsClick = async( ) => {

        setIsLoadingForDevice( true );

        const byWifiKeyDto = await getResidentByWifiKey( wifiKeyDto.psk );

        setIsLoadingForDevice( false );

        if( byWifiKeyDto ) {
            
            if( deviceSessionsClick ){
                deviceSessionsClick( byWifiKeyDto.id );
            }

        }

    }
   

    const handleMoreWifiDetailsDialogOk = ( ) => setMoreWifiDetailsDialogOpen( false );


    const handlePasswordResetClick = ( ) => setSelectVillageDialogOpen( true );


    const handleSelectVillageDialogCancel = ( ) => setSelectVillageDialogOpen( false );


    const handleMoreClick = async( ) => {

        setIsLoading( true );

        const byWifiKeyDto = await getResidentByWifiKey( wifiKeyDto.psk );

        setIsLoading( false );

        if( byWifiKeyDto ) {
            
            setByWifiKeyDto( byWifiKeyDto );
            setMoreWifiDetailsDialogOpen( true );

        }

    }


    const handleSelectVillageDialogOk = ( village: string ) => {

        setSelectVillageDialogOpen( false );
        resetWifiKey( village );

    };


    const resetWifiKey = async ( village : string ) => {

        if( !resident ) {

            return;

        }
        
        setIsResettingPassword( true );
        
        const wifiKeyDto = await resetResidentWifiKey( {
            
            createdBy : state.username
            , employeeNumber : resident.employeeId
            , villageCode : village
            
        });

        setIsResettingPassword( false );

        if( wifiKeyDto ) {

            setWifiKeyDto( wifiKeyDto );

        }

    };


    return (

        <>
            <Stack direction="row" sx={{ marginTop: '20px' }}>

                <Card sx={{ boxShadow: 5, maxWidth: 'fit-content' }}>

                    <CardHeader 
                        sx={{ fontWeight: '600', fontSize: '20px', paddingTop: '5px', paddingBottom: '0' }}
                        title="Wi-Fi Details"
                        avatar={
                            <WifiIcon />
                        }
                        action={
                            <Tooltip title="More Details">
                                <>
                                    {

                                    isLoading
                                        ?
                                    <Box display="flex" justifyContent="center">
                                        <CircularProgress size="30px" sx={{ marginTop: '5px', color: '#e65400'}}/>
                                    </Box>
                                        :
                                    <IconButton 
                                        disabled={ isLoadingForDevice }
                                        onClick={ handleMoreClick }
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                    }
                                </>
                            </Tooltip>
                        }
                    />

                    <CardContent>

                    <Stack>

                        <TextField 
                            sx={ textFieldSx } 
                            label="Network Name" 
                            variant="outlined"
                            value={ wifiKeyDto.ssid }
                            disabled
                        />
                    
                        <TextField 
                            sx={ textFieldSx } 
                            label="Password" 
                            variant="outlined"
                            value={ wifiKeyDto.psk }
                            disabled
                        />
                    
                        <TextField
                            sx={ textFieldSx } 
                            label="Expiry" 
                            variant="outlined"
                            value={ wifiKeyDto.expiryDate ? formatDateTime( wifiKeyDto.expiryDate ) : 'Never' }
                            disabled
                        />

                    </Stack>

                    </CardContent>

                    {
                        resident
                            &&
                        <CardActions
                            sx={{ display: "flex" }}
                        >
                            
                            <LoadingButton
                                sx={{ fontWeight: 'bold', height: '55px', marginLeft: '10px', textTransform: 'none', width: '200px' }}
                                loadingPosition="start"
                                startIcon={ <SmartphoneIcon /> }
                                variant="contained"
                                onClick={ handleDeviceSessionsClick }
                                disabled={ isResettingPassword }
                                loading={ isLoadingForDevice }
                            >
                                Device
                            </LoadingButton>

                            <Box sx={{ flexGrow: 1 }} />

                            <LoadingButton
                                sx={{ fontWeight: 'bold', height: '55px', marginRight: '10px', textTransform: 'none', width: '200px' }}
                                loadingPosition="start"
                                startIcon={ <LockResetIcon /> }
                                variant="contained"
                                onClick={ handlePasswordResetClick }
                                loading={ isResettingPassword }
                                disabled={ isLoadingForDevice }
                            >
                                Reset Password
                            </LoadingButton>

                        </CardActions>
                    }

                </Card>

                <QRCodeCanvas value={ wifiKey } size={ 350 } style={{ marginLeft: '15px' }} />

            </Stack>

    
            <ByWifiKeyDialog
                byWifiKeyDto={ byWifiKeyDto as ByWifiKeyDto }
                dialogOpen={ moreWifiDetailsDialogOpen }
                handleOk={ handleMoreWifiDetailsDialogOk } 
                wifiKey={ wifiKeyDto.psk }
            />
            

            { 
                resident
                    &&
                <SelectVillageDialog 
                    asset={ resident.asset }
                    dialogOpen={ selectVillageDialogOpen }
                    handleCancel={ handleSelectVillageDialogCancel }
                    handleOk={ handleSelectVillageDialogOk }  
                />
            }
            
        </>

    );

};